import { CouponRow, PriceRow } from 'server/types';
import {
  PurchaseFn,
  StripeHandlerFn,
  PriceSummary,
  AipBackupOfferDisplay,
} from 'shared/payments/types';
import { CheckoutFormWizard } from '../sales-checkout/checkout-form-wizard';
import { UrgencyBar } from './urgency-bar';
import { PriceToggle } from './price-toggle';
import { useState } from 'preact/hooks';

type TestimonialItem = {
  quote: string;
  name: string;
  title?: string;
  photoUrl?: string;
};

type Props = {
  courseId: string;
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
  pricingNote?: string;
  deadlineText?: string;
  ctaText?: string;
  testimonials?: TestimonialItem[];
  contactPhone?: string;
  contactEmail?: string;
  alternativePrices?: PriceSummary[];
  campaignSlug?: string;
  testModeToken?: string;
  aipBackupOffer?: AipBackupOfferDisplay;
  simpleCheckConsent?: boolean;
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function TestimonialCard({ quote, name, photoUrl }: TestimonialItem) {
  return (
    <div class="testimonial-mini">
      <p class="testimonial-mini__quote">&ldquo;{quote}&rdquo;</p>
      <div class="testimonial-mini__footer">
        {photoUrl ? (
          <img src={photoUrl} alt="" class="testimonial-mini__avatar" style="object-fit: cover" />
        ) : (
          <span class="testimonial-mini__avatar">{getInitials(name)}</span>
        )}
        <span class="testimonial-mini__name">{name}</span>
      </div>
    </div>
  );
}

export function IntegratedCheckout({
  courseId,
  price,
  coupon,
  collectAddress,
  handleStripe,
  beginPurchase,
  onPurchaseComplete,
  pricingNote,
  deadlineText,
  ctaText,
  testimonials,
  contactPhone,
  contactEmail,
  alternativePrices,
  campaignSlug,
  testModeToken,
  aipBackupOffer,
  simpleCheckConsent,
}: Props) {
  // Initialized with total: 0 to hide the step indicator until the wizard
  // reports its actual step count via onStepChange
  const [progress, setProgress] = useState({ step: 0, total: 0 });

  return (
    <div id="checkout" style="max-width: 860px; margin: 0 auto; padding: 0 20px 48px">
      {deadlineText && <UrgencyBar text={deadlineText} />}

      <div class="integrated-card">
        {alternativePrices && alternativePrices.length > 1 && (
          <PriceToggle
            prices={alternativePrices}
            activePriceId={price.id}
            slug={campaignSlug}
            coupon={coupon}
            testModeToken={testModeToken}
          />
        )}
        <div
          class="campaign-progress-bar"
          style={`--progress: ${progress.total > 0 ? ((progress.step + 1) / progress.total) * 100 : 50}%`}
        />
        <div class="integrated-card__form">
          {progress.total > 1 && (
            <div class="campaign-steps">
              {['Your Info', 'Payment'].slice(0, progress.total).map((label, i) => (
                <div
                  key={i}
                  class={`campaign-steps__item ${i === progress.step ? 'campaign-steps__item--active' : ''} ${i < progress.step ? 'campaign-steps__item--done' : ''}`}
                >
                  <span class="campaign-steps__number">{i < progress.step ? '\u2713' : i + 1}</span>
                  <span class="campaign-steps__label">{label}</span>
                  {i < progress.total - 1 && (
                    <svg
                      class="campaign-steps__arrow"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
          <CheckoutFormWizard
            variant="campaign"
            courseId={courseId}
            price={price}
            coupon={coupon}
            collectAddress={collectAddress}
            handleStripe={handleStripe}
            beginPurchase={beginPurchase}
            onPurchaseComplete={onPurchaseComplete}
            pricingNote={pricingNote}
            actionText={ctaText}
            onStepChange={(step, total) => setProgress({ step, total })}
            testModeToken={testModeToken}
            campaignSlug={campaignSlug}
            aipBackupOffer={aipBackupOffer}
            explicitCurrency={new Set(alternativePrices?.map((p) => p.currency)).size > 1}
            simpleCheckConsent={simpleCheckConsent}
          />

          {(contactPhone || contactEmail) && (
            <div class="contact-strip" style="margin-top: 20px; justify-content: center">
              {contactPhone && (
                <a href={`tel:${contactPhone}`} class="contact-strip__item">
                  <span class="contact-strip__icon">
                    <PhoneIcon />
                  </span>
                  <span style="font-weight: 600; color: var(--m-blue)">{contactPhone}</span>
                </a>
              )}
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} class="contact-strip__item">
                  <span class="contact-strip__icon">
                    <MailIcon />
                  </span>
                  <span style="font-weight: 600; color: var(--m-blue)">{contactEmail}</span>
                </a>
              )}
            </div>
          )}
        </div>

        {testimonials && testimonials.length > 0 && (
          <>
            <div class="integrated-card__divider" />
            <div class="integrated-card__social">
              <div class="integrated-card__social-header">
                <span>What others are saying</span>
              </div>
              <div class="testimonial-cards">
                {testimonials.map((t, i) => (
                  <TestimonialCard key={i} {...t} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
